import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { AdminPageHeader, Field, Select, Toggle } from "./_helpers";
import { toast } from "sonner";
import { Plus, Trash2, Search, CheckSquare, Square, X } from "lucide-react";

export default function AdminSettings() {
  const [tab, setTab] = useState("integrations");
  const [integrations, setIntegrations] = useState({});
  const [finance, setFinance] = useState({ items: [], mode: "manual", enabled: true });
  const [provinces, setProvinces] = useState([]);
  // Multi-province weather setup — an admin can select N provinces, tweak
  // one shared config, and apply it to all of them with a single click.
  const [weatherProvs, setWeatherProvs] = useState([]); // array of province IDs
  const [weatherQuery, setWeatherQuery] = useState("");
  const [weather, setWeather] = useState({});
  const [weatherPreviewProv, setWeatherPreviewProv] = useState(""); // single id for "Şimdi çek"
  const [social, setSocial] = useState({});
  const [hotline, setHotline] = useState({});
  const [branding, setBranding] = useState({});

  useEffect(() => {
    api.get("/integrations").then((r) => setIntegrations(r.data));
    api.get("/finance").then((r) => setFinance(r.data));
    api.get("/social-media").then((r) => setSocial(r.data || {}));
    api.get("/hotline-button").then((r) => setHotline(r.data || {}));
    api.get("/settings/branding").then((r) => setBranding(r.data || {}));
    api.get("/provinces?active_only=false").then((r) => setProvinces(r.data));
  }, []);

  // When provinces are first selected, pre-fill the form from the first
  // province's current settings so the admin isn't editing from a blank
  // slate. Switching selection does NOT clobber in-progress edits.
  const [weatherLoadedFor, setWeatherLoadedFor] = useState(null);
  useEffect(() => {
    if (!weatherProvs.length) {
      setWeatherLoadedFor(null);
      return;
    }
    const first = weatherProvs[0];
    if (weatherLoadedFor === first) return;
    api.get(`/weather?province_id=${first}`).then((r) => {
      setWeather(r.data || {});
      setWeatherLoadedFor(first);
    });
    if (!weatherPreviewProv) setWeatherPreviewProv(first);
  }, [weatherProvs, weatherLoadedFor, weatherPreviewProv]);

  const filteredProvinces = useMemo(() => {
    const q = (weatherQuery || "").trim().toLocaleLowerCase("tr");
    if (!q) return provinces;
    return provinces.filter((p) => p.name.toLocaleLowerCase("tr").includes(q));
  }, [provinces, weatherQuery]);

  const toggleProvince = (id) => {
    setWeatherProvs((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const tabs = [
    ["integrations", "Entegrasyonlar"],
    ["finance", "Döviz/Altın"],
    ["weather", "Hava Durumu"],
    ["social", "Sosyal Medya"],
    ["hotline", "Haber Hattı Butonu"],
    ["branding", "Marka"],
  ];

  return (
    <div>
      <AdminPageHeader title="Sistem Ayarları" />
      <div className="flex gap-2 mb-4 flex-wrap">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded text-sm ${tab === k ? "bg-[#e11d2a] text-white" : "border"}`}>{l}</button>
        ))}
      </div>

      {tab === "integrations" && (
        <div className="bg-white border border-gray-100 rounded-lg p-4 space-y-4">
          {Object.entries(integrations).map(([k, v]) => (
            <div key={k} className="border border-gray-100 rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold capitalize">{k}</div>
                <Toggle label="Aktif" value={v.enabled} onChange={(b) => setIntegrations({ ...integrations, [k]: { ...v, enabled: b } })} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Select label="Mod" value={v.mode} options={k === "pharmacy" ? [
                  { value: "manual", label: "Manuel" },
                  { value: "scraper", label: "Otomatik (eczaneler.gen.tr)" },
                  { value: "api", label: "API" },
                ] : [{ value: "manual", label: "Manuel" }, { value: "api", label: "API" }]} onChange={(e) => setIntegrations({ ...integrations, [k]: { ...v, mode: e.target.value } })} />
                <Field label="API URL" value={v.api_url || ""} onChange={(e) => setIntegrations({ ...integrations, [k]: { ...v, api_url: e.target.value } })} />
                <Field label="API Key" value={v.api_key || ""} onChange={(e) => setIntegrations({ ...integrations, [k]: { ...v, api_key: e.target.value } })} />
              </div>
            </div>
          ))}
          <button onClick={async () => { await api.put("/integrations", integrations); toast.success("Kaydedildi"); }} className="px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold">Kaydet</button>
        </div>
      )}

      {tab === "finance" && (
        <div className="bg-white border border-gray-100 rounded-lg p-4">
          <div className="flex gap-2 mb-3 items-center flex-wrap">
            <Select
              label="Mod"
              value={finance.mode || "manual"}
              options={[
                { value: "manual", label: "Manuel" },
                { value: "api", label: "API (özel)" },
                { value: "tcmb", label: "TCMB Otomatik" },
              ]}
              onChange={(e) => setFinance({ ...finance, mode: e.target.value })}
            />
            <Toggle label="Aktif" value={finance.enabled !== false} onChange={(v) => setFinance({ ...finance, enabled: v })} />
          </div>

          {finance.mode === "tcmb" && (
            <div className="bg-blue-50 border border-blue-100 rounded-md p-3 mb-3 text-sm">
              <div className="font-bold mb-1 flex items-center gap-1">🇹🇷 TCMB Otomatik Mod</div>
              <p className="text-xs text-gray-600 mb-2">
                Kurlar her gün <code className="bg-white px-1 rounded">tcmb.gov.tr/kurlar/today.xml</code> adresinden otomatik çekilir.
                Sunucu içi 30 dk cache + günde 2 kere (09:00 & 16:00) otomatik yenileme. API anahtarı gerekmez.
                {finance.tcmb_date && <span className="ml-2 text-emerald-700 font-semibold">Son veri: {finance.tcmb_date}</span>}
              </p>
              <div className="text-xs text-gray-700 mb-1">Gösterilecek Para Birimleri (TCMB kodları):</div>
              <input
                type="text"
                value={(finance.tcmb_codes || ["USD","EUR","GBP","CHF","JPY","SAR"]).join(", ")}
                onChange={(e) => setFinance({ ...finance, tcmb_codes: e.target.value.split(",").map(s => s.trim().toUpperCase()).filter(Boolean) })}
                className="w-full px-2 py-1.5 border rounded text-sm"
                placeholder="USD, EUR, GBP, CHF, JPY, SAR"
                data-testid="finance-tcmb-codes"
              />
              <div className="mt-2 flex gap-2">
                <button
                  onClick={async () => {
                    try {
                      const r = await api.post("/finance/refresh-tcmb");
                      toast.success(`TCMB güncel veri çekildi: ${r.data.tcmb_date} (${r.data.items.length} para birimi)`);
                      api.get("/finance").then((rr) => setFinance(rr.data));
                    } catch { toast.error("TCMB çekilemedi"); }
                  }}
                  className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded font-semibold"
                  data-testid="finance-tcmb-refresh"
                >
                  Şimdi Çek (önizleme)
                </button>
              </div>
              {(finance.items || []).length > 0 && (
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {finance.items.map((it, i) => (
                    <div key={i} className="bg-white border rounded p-2 text-xs flex items-center justify-between">
                      <span className="font-bold">{it.code || it.name}</span>
                      <span className="text-gray-700">{it.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {finance.mode !== "tcmb" && (
            <>
              {(finance.items || []).map((it, i) => (
                <div key={i} className="grid grid-cols-4 gap-2 mb-2">
                  <input value={it.name} onChange={(e) => { const arr = [...finance.items]; arr[i].name = e.target.value; setFinance({ ...finance, items: arr }); }} placeholder="Ad" className="px-3 py-2 border border-gray-200 rounded text-sm" />
                  <input value={it.value} onChange={(e) => { const arr = [...finance.items]; arr[i].value = e.target.value; setFinance({ ...finance, items: arr }); }} placeholder="Değer" className="px-3 py-2 border border-gray-200 rounded text-sm" />
                  <input value={it.change} onChange={(e) => { const arr = [...finance.items]; arr[i].change = e.target.value; setFinance({ ...finance, items: arr }); }} placeholder="Değişim (+/-)" className="px-3 py-2 border border-gray-200 rounded text-sm" />
                  <button onClick={() => setFinance({ ...finance, items: finance.items.filter((_, j) => j !== i) })} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              <button onClick={() => setFinance({ ...finance, items: [...(finance.items || []), { name: "", value: "", change: "" }] })} className="text-xs text-blue-600 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Satır ekle</button>
            </>
          )}

          <div className="mt-3">
            <button
              onClick={async () => {
                const payload = { ...finance };
                // when in tcmb mode, items are computed server-side, so don't send them as user data
                if (payload.mode === "tcmb") delete payload.items;
                await api.put("/finance", payload);
                toast.success("Kaydedildi");
                api.get("/finance").then((r) => setFinance(r.data));
              }}
              className="px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold"
              data-testid="finance-save"
            >
              Kaydet
            </button>
          </div>
        </div>
      )}

      {tab === "weather" && (
        <div className="bg-white border border-gray-100 rounded-lg p-4 space-y-3" data-testid="admin-weather-tab">
          {/* Multi-province picker.
           *  - Instant filter at the top, "tümünü seç" / "temizle" shortcuts
           *  - Clicking a tile toggles its membership; selected ones show
           *    a red chip row above for easy removal
           *  - Admin can tweak a single form and apply it to the whole set. */}
          <div className="border border-gray-100 rounded-md p-3">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <div className="text-sm font-bold">
                İller{" "}
                <span className="text-xs font-normal text-gray-500">
                  ({weatherProvs.length} seçili / {provinces.length} toplam)
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setWeatherProvs(filteredProvinces.map((p) => p.id))}
                  className="px-2 py-1 border rounded hover:bg-gray-50"
                  data-testid="weather-select-all"
                >
                  Filtrelenenleri Seç
                </button>
                <button
                  type="button"
                  onClick={() => setWeatherProvs([])}
                  className="px-2 py-1 border rounded hover:bg-gray-50"
                  disabled={!weatherProvs.length}
                  data-testid="weather-select-clear"
                >
                  Seçimi Temizle
                </button>
              </div>
            </div>

            <div className="relative mb-2">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={weatherQuery}
                onChange={(e) => setWeatherQuery(e.target.value)}
                placeholder="İl ara…"
                className="w-full pl-8 pr-7 py-1.5 border border-gray-200 rounded text-sm"
                data-testid="weather-province-search"
              />
              {weatherQuery && (
                <button
                  type="button"
                  onClick={() => setWeatherQuery("")}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-700"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Selected chip row — gives the admin instant feedback and a
             *   fast way to remove individual choices from a long list. */}
            {weatherProvs.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2" data-testid="weather-selected-chips">
                {weatherProvs.map((id) => {
                  const p = provinces.find((x) => x.id === id);
                  if (!p) return null;
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#e11d2a] text-white rounded-full text-xs font-semibold"
                    >
                      {p.name}
                      <button
                        type="button"
                        onClick={() => toggleProvince(id)}
                        className="hover:bg-white/20 rounded-full p-0.5"
                        title="Kaldır"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            <div className="max-h-56 overflow-y-auto grid grid-cols-2 md:grid-cols-4 gap-1 border rounded p-2 bg-gray-50" data-testid="weather-province-grid">
              {filteredProvinces.map((p) => {
                const checked = weatherProvs.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggleProvince(p.id)}
                    className={`flex items-center gap-1.5 text-sm px-2 py-1 rounded text-left transition ${
                      checked
                        ? "bg-[#e11d2a]/10 border border-[#e11d2a]/40 text-[#e11d2a] font-semibold"
                        : "bg-white border border-transparent hover:border-gray-200"
                    }`}
                    data-testid={`weather-province-${p.slug || p.id}`}
                  >
                    {checked ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4 text-gray-400" />}
                    <span className="truncate">{p.name}</span>
                  </button>
                );
              })}
              {filteredProvinces.length === 0 && (
                <div className="col-span-full text-center text-xs text-gray-500 py-4">
                  Arama ile eşleşen il yok
                </div>
              )}
            </div>
          </div>

          {weatherProvs.length > 0 && (
            <>
              <div className="flex gap-3 items-center flex-wrap">
                <Select
                  label="Mod"
                  value={weather.mode || "manual"}
                  options={[
                    { value: "manual", label: "Manuel" },
                    { value: "api", label: "API (özel)" },
                    { value: "auto", label: "Open-Meteo Otomatik" },
                  ]}
                  onChange={(e) => setWeather({ ...weather, mode: e.target.value })}
                />
                <Toggle label="Aktif" value={weather.enabled !== false} onChange={(v) => setWeather({ ...weather, enabled: v })} />
                <div className="text-xs text-gray-500 ml-auto">
                  Aşağıdaki ayarlar <strong>{weatherProvs.length}</strong> ile uygulanacak.
                </div>
              </div>

              {weather.mode === "auto" && (
                <div className="bg-cyan-50 border border-cyan-100 rounded p-3 text-sm">
                  <div className="font-bold flex items-center gap-1 mb-1">🌤️ Open-Meteo Otomatik Mod</div>
                  <p className="text-xs text-gray-600 mb-2">
                    İl merkez koordinatlarından <code className="bg-white px-1 rounded">api.open-meteo.com</code> üzerinden anlık veri çekilir.
                    API anahtarı gerekmez. 15 dk cache. {weather.label && <span className="text-emerald-700 font-semibold">Son veri: {weather.label}</span>}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select
                      label="Önizleme ili"
                      value={weatherPreviewProv || weatherProvs[0]}
                      options={weatherProvs.map((id) => {
                        const p = provinces.find((x) => x.id === id);
                        return { value: id, label: p?.name || id };
                      })}
                      onChange={(e) => setWeatherPreviewProv(e.target.value)}
                    />
                    <button
                      onClick={async () => {
                        try {
                          const id = weatherPreviewProv || weatherProvs[0];
                          const r = await api.post(`/weather/refresh-auto?province_id=${id}`);
                          toast.success(`Çekildi: ${r.data.temperature}°C - ${r.data.label}`);
                          setWeather(r.data);
                        } catch (e) { toast.error(e.response?.data?.detail || "Çekilemedi"); }
                      }}
                      className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded font-semibold self-end"
                      data-testid="weather-refresh-auto"
                    >
                      Şimdi Çek (önizleme)
                    </button>
                  </div>
                  {weather.temperature != null && (
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="bg-white border rounded p-2 text-xs"><div className="text-gray-500">Sıcaklık</div><div className="font-bold text-lg">{weather.temperature}°C</div></div>
                      <div className="bg-white border rounded p-2 text-xs"><div className="text-gray-500">Hissedilen</div><div className="font-bold">{weather.feels_like}°C</div></div>
                      <div className="bg-white border rounded p-2 text-xs"><div className="text-gray-500">Nem</div><div className="font-bold">%{weather.humidity}</div></div>
                      <div className="bg-white border rounded p-2 text-xs"><div className="text-gray-500">Rüzgar</div><div className="font-bold">{weather.wind_speed} km/sa</div></div>
                      <div className="bg-white border rounded p-2 text-xs col-span-2"><div className="text-gray-500">Bugün</div><div className="font-bold">Yüksek: {weather.high}°C · Düşük: {weather.low}°C</div></div>
                    </div>
                  )}
                </div>
              )}

              {weather.mode !== "auto" && (
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Sıcaklık" type="number" value={weather.temperature ?? ""} onChange={(e) => setWeather({ ...weather, temperature: +e.target.value })} />
                  <Select label="Durum" value={weather.condition || ""} options={["sunny", "partly_cloudy", "cloudy", "rainy", "snowy"].map((c) => ({ value: c, label: c }))} onChange={(e) => setWeather({ ...weather, condition: e.target.value })} />
                  <Field label="Nem (%)" type="number" value={weather.humidity ?? ""} onChange={(e) => setWeather({ ...weather, humidity: +e.target.value })} />
                  <Field label="Rüzgar (km/sa)" type="number" value={weather.wind_speed ?? ""} onChange={(e) => setWeather({ ...weather, wind_speed: +e.target.value })} />
                </div>
              )}

              <button
                onClick={async () => {
                  const body = weather.mode === "auto"
                    ? { mode: "auto", enabled: weather.enabled !== false }
                    : weather;
                  // Apply the same payload to every selected province in
                  // parallel; show a short summary toast at the end.
                  try {
                    const results = await Promise.allSettled(
                      weatherProvs.map((id) => api.put(`/weather?province_id=${id}`, body))
                    );
                    const ok = results.filter((r) => r.status === "fulfilled").length;
                    const failed = results.length - ok;
                    if (failed === 0) {
                      toast.success(`${ok} il için kaydedildi`);
                    } else {
                      toast.error(`${ok} başarılı, ${failed} başarısız`);
                    }
                  } catch (e) {
                    toast.error("Kaydedilemedi");
                  }
                }}
                className="px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold"
                data-testid="weather-save"
              >
                Seçili {weatherProvs.length} İle Kaydet
              </button>
            </>
          )}
        </div>
      )}

      {tab === "social" && (
        <div className="bg-white border border-gray-100 rounded-lg p-4 grid md:grid-cols-2 gap-2">
          {["facebook", "instagram", "twitter", "youtube", "tiktok", "threads", "whatsapp_channel", "telegram"].map((k) => (
            <Field key={k} label={k} value={social[k] || ""} onChange={(e) => setSocial({ ...social, [k]: e.target.value })} />
          ))}
          <button onClick={async () => { await api.put("/social-media", social); toast.success("Kaydedildi"); }} className="md:col-span-2 px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold">Kaydet</button>
        </div>
      )}

      {tab === "hotline" && (
        <div className="bg-white border border-gray-100 rounded-lg p-4 grid md:grid-cols-2 gap-2">
          <Toggle label="Buton Aktif" value={hotline.is_active} onChange={(v) => setHotline({ ...hotline, is_active: v })} />
          <Field label="Telefon (+90...)" value={hotline.phone_number || ""} onChange={(e) => setHotline({ ...hotline, phone_number: e.target.value })} />
          <Field label="Varsayılan Mesaj" value={hotline.default_message || ""} onChange={(e) => setHotline({ ...hotline, default_message: e.target.value })} />
          <Field label="Renk (#HEX)" value={hotline.color || ""} onChange={(e) => setHotline({ ...hotline, color: e.target.value })} />
          <button onClick={async () => { await api.put("/hotline-button", hotline); toast.success("Kaydedildi"); }} className="md:col-span-2 px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold">Kaydet</button>
        </div>
      )}

      {tab === "branding" && (
        <div className="bg-white border border-gray-100 rounded-lg p-4 grid md:grid-cols-2 gap-2">
          <Field label="Site adı" value={branding.site_name || ""} onChange={(e) => setBranding({ ...branding, site_name: e.target.value })} />
          <Field label="Site alt yazı" value={branding.site_subtitle || ""} onChange={(e) => setBranding({ ...branding, site_subtitle: e.target.value })} />
          <div className="md:col-span-2">
            <Field
              label="Site URL (ör: https://sine81.com) — şifre sıfırlama linkleri için kullanılır"
              value={branding.site_url || ""}
              onChange={(e) => setBranding({ ...branding, site_url: e.target.value })}
              data-testid="branding-site-url"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Boş bırakılırsa sunucu kendi iç host'unu kullanır; mail'deki link
              bu durumda ziyaretçiler için erişilemez olabilir.
            </p>
          </div>
          <Field label="Slogan" value={branding.slogan || ""} onChange={(e) => setBranding({ ...branding, slogan: e.target.value })} />
          <Field label="Varsayılan il slug" value={branding.default_province_slug || ""} onChange={(e) => setBranding({ ...branding, default_province_slug: e.target.value })} />
          <Field label="İletişim email" value={branding.contact_email || ""} onChange={(e) => setBranding({ ...branding, contact_email: e.target.value })} />
          <Field label="İletişim telefon" value={branding.contact_phone || ""} onChange={(e) => setBranding({ ...branding, contact_phone: e.target.value })} />
          <button onClick={async () => { await api.put("/branding", branding); toast.success("Kaydedildi"); }} className="md:col-span-2 px-4 py-2 bg-[#e11d2a] text-white rounded text-sm font-semibold">Kaydet</button>
        </div>
      )}
    </div>
  );
}
